import { useDispatch } from 'react-redux';
import axios from '../utils/axiosConfig';
import { errorHandler } from '../utils/errorHandler';
import {
  setViews,
  setViewsLoading,
  setViewsError,
  addView,
  removeView,
} from '../redux/slices/views';
import { View, ViewCreatePayload, ViewUpdatePayload } from '../features/SavedViews/SavedViews.def';
import toast from 'react-hot-toast';

interface ViewsListResponse {
  views: View[];
}

const useViewsService = () => {
  const dispatch = useDispatch();

  const showError = (error: unknown) => {
    errorHandler.showError(error);
  };

  const fetchViews = async (component?: string) => {
    dispatch(setViewsLoading(true));
    try {
      const params = component ? `?component=${component}` : '';
      const response = await axios.get<ViewsListResponse>(`/resource/views${params}`);
      dispatch(setViews(response?.data?.views || []));
    } catch (error) {
      showError(error);
      dispatch(setViewsError(error instanceof Error ? error.message : 'Failed to fetch views'));
    } finally {
      dispatch(setViewsLoading(false));
    }
  };

  const createView = async (payload: ViewCreatePayload): Promise<View | undefined> => {
    try {
      const response = await axios.post<View>('/resource/views', payload);
      dispatch(addView(response?.data));
      toast.success('View saved successfully');
      return response?.data;
    } catch (error) {
      showError(error);
      throw error;
    }
  };

  const updateView = async (viewId: number, payload: ViewUpdatePayload): Promise<View | undefined> => {
    try {
      const response = await axios.put<View>(`/resource/views/${viewId}`, payload);
      toast.success('View updated successfully');
      return response?.data;
    } catch (error) {
      showError(error);
      throw error;
    }
  };

  const deleteView = async (viewId: number) => {
    try {
      await axios.delete(`/resource/views/${viewId}`);
      dispatch(removeView(viewId));
      toast.success('View deleted successfully');
    } catch (error) {
      showError(error);
      throw error;
    }
  };

  return {
    fetchViews,
    createView,
    updateView,
    deleteView,
  };
};

export default useViewsService;
